import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Button, TextField, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Tooltip, alpha, useTheme,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from '@mui/material';
import { ArrowLeft, BarChart3, UserPlus, Clock, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { backendClient } from '../../../../api/backend';
import EditSubscriptionDialog from '../../../SettingsScreen/components/PlatformManagement/dialogs/EditSubscriptionDialog';
import CustomAlert from '../../../SettingsScreen/components/UserManagement/CustomAlert';

const OrganizationDetailPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoles, setInviteRoles] = useState(['user']);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });

  const colors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.success.main,
    info: theme.palette.info.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    border: theme.palette.divider,
    backgroundAlt: theme.palette.action.hover,
    isDark: theme.palette.mode === 'dark',
  };

  const showAlert = (message, severity = 'success') => {
    setAlertInfo({ open: true, message, severity });
  };

  const fetchOrg = useCallback(async () => {
    try {
      setLoading(true);
      const data = await backendClient.getAdminOrganization(orgId);
      setOrganization(data);
      setEditName(data.name || '');
      setEditDomain(data.domain?.join(', ') || '');
    } catch (error) {
      showAlert(error.message || 'Error fetching organization', 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleSaveOrg = async () => {
    const data = { name: editName.trim() };
    data.domain = editDomain.trim()
      ? editDomain.split(',').map((d) => d.trim()).filter(Boolean)
      : [];
    try {
      await backendClient.updateAdminOrganization(orgId, data);
      showAlert(t('organization_updated', 'Organization updated'));
      setEditing(false);
      fetchOrg();
    } catch (error) {
      showAlert(error.message || 'Error updating organization', 'error');
    }
  };

  const handleCreateSubscription = async (oId, data) => {
    try {
      await backendClient.createAdminSubscription(oId, data);
      showAlert(t('subscription_created', 'Subscription created'));
      fetchOrg();
    } catch (error) {
      showAlert(error.message || 'Error creating subscription', 'error');
    }
  };

  const handleUpdateSubscription = async (oId, data) => {
    try {
      await backendClient.updateAdminSubscription(oId, data);
      showAlert(t('subscription_updated', 'Subscription updated'));
      fetchOrg();
    } catch (error) {
      showAlert(error.message || 'Error updating subscription', 'error');
    }
  };

  const availableRoles = ['user', 'admin', 'billing_admin'];

  const handleInviteRoleChange = (event) => {
    const newRoles = event.target.value;
    const lastAdded = newRoles.find((r) => !inviteRoles.includes(r));
    if (lastAdded === 'user') {
      setInviteRoles(newRoles.filter((r) => r !== 'admin' && r !== 'billing_admin'));
    } else if (['admin', 'billing_admin'].includes(lastAdded) && newRoles.includes('user')) {
      setInviteRoles(newRoles.filter((r) => r !== 'user'));
    } else {
      setInviteRoles(newRoles);
    }
  };

  const handleInviteSubmit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)) {
      setInviteError(t('invalid_email', 'Please enter a valid email address'));
      return;
    }
    if (!inviteRoles.length) {
      setInviteError(t('select_at_least_one_role', 'Please select at least one role'));
      return;
    }
    setInviteLoading(true);
    setInviteError('');
    try {
      await backendClient.inviteAdminUser({ email: inviteEmail, organization_id: orgId, roles: inviteRoles });
      showAlert(t('invitation_sent', 'Invitation sent successfully'));
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRoles(['user']);
      fetchOrg();
    } catch (err) {
      setInviteError(err.message || 'Error sending invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await backendClient.resendAdminInvitation(invitation.id);
      showAlert(t('invitation_resent', 'Invitation resent to {{email}}', { email: invitation.email }));
      fetchOrg();
    } catch (err) {
      showAlert(err.message || 'Error resending invitation', 'error');
    }
  };

  const handleDeleteInvitation = async (invitation) => {
    if (!window.confirm(t('confirm_delete_invitation', 'Delete invitation for {{email}}?', { email: invitation.email }))) {
      return;
    }
    try {
      await backendClient.deleteAdminInvitation(invitation.id);
      showAlert(t('invitation_deleted', 'Invitation deleted'));
      fetchOrg();
    } catch (err) {
      showAlert(err.message || 'Error deleting invitation', 'error');
    }
  };

  const getRoleChipColor = (role) => {
    switch (role) {
      case 'super_admin': return { bg: alpha(colors.warning, 0.12), color: colors.warning };
      case 'admin': return { bg: alpha(colors.secondary, 0.12), color: colors.secondary };
      case 'billing_admin': return { bg: alpha(colors.info, 0.12), color: colors.info };
      default: return { bg: colors.backgroundAlt, color: colors.textSecondary };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return { bg: alpha(colors.secondary, 0.12), text: colors.secondary };
      case 'trial': return { bg: alpha(colors.info, 0.12), text: colors.info };
      case 'cancelled': return { bg: alpha(colors.warning, 0.12), text: colors.warning };
      default: return { bg: alpha(colors.error, 0.12), text: colors.error };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Organization not found.</Typography>
        <Button onClick={() => navigate('/admin/organizations')} sx={{ mt: 2 }}>Go back</Button>
      </Box>
    );
  }

  const sub = organization.subscription;

  return (
    <Box sx={{ py: 2, height: '100%', overflow: 'auto' }}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/organizations')} size="small">
          <ArrowLeft size={20} />
        </IconButton>
        <Typography
          variant="body2"
          sx={{ color: colors.textSecondary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/admin/organizations')}
        >
          Organizations
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>/</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text }}>
          {organization.name}
        </Typography>
      </Box>

      {/* Org Info Card */}
      <Box sx={{
        p: 3, mb: 3, borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.text }}>
            {t('organization_info', 'Organization Info')}
          </Typography>
          {!editing ? (
            <Button size="small" startIcon={<Edit />} onClick={() => setEditing(true)}
              sx={{ textTransform: 'none', color: colors.primary }}>
              {t('edit', 'Edit')}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<Save />} onClick={handleSaveOrg}
                variant="contained" disableElevation
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: colors.primary }}>
                {t('save', 'Save')}
              </Button>
              <Button size="small" startIcon={<Cancel />}
                onClick={() => { setEditing(false); setEditName(organization.name); setEditDomain(organization.domain?.join(', ') || ''); }}
                sx={{ textTransform: 'none', color: colors.textSecondary }}>
                {t('cancel', 'Cancel')}
              </Button>
            </Box>
          )}
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={t('name', 'Name')} value={editName} onChange={(e) => setEditName(e.target.value)}
              size="small" sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
            <TextField label={t('domains', 'Domains')} value={editDomain} onChange={(e) => setEditDomain(e.target.value)}
              size="small" placeholder="@acme.com, @acme.org"
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{t('name', 'Name')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text }}>{organization.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{t('domains', 'Domains')}</Typography>
              <Typography variant="body2" sx={{ color: colors.text }}>{organization.domain?.join(', ') || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>{t('created', 'Created')}</Typography>
              <Typography variant="body2" sx={{ color: colors.text }}>
                {organization.created_at ? new Date(organization.created_at).toLocaleDateString() : '—'}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Subscription Card */}
      <Box sx={{
        p: 3, mb: 3, borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.text }}>
            {t('subscription', 'Subscription')}
          </Typography>
          <Button size="small" startIcon={<Edit />} onClick={() => setSubDialogOpen(true)}
            sx={{ textTransform: 'none', color: colors.primary }}>
            {sub ? t('edit_subscription', 'Edit Subscription') : t('create_subscription', 'Create Subscription')}
          </Button>
        </Box>

        {sub ? (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[
              { label: t('plan', 'Plan'), value: sub.plan, capitalize: true },
              { label: t('billing_cycle', 'Billing Cycle'), value: sub.type, capitalize: true },
              { label: t('status.label', 'Status'), chip: true, chipProps: getStatusColor(sub.status), value: sub.status },
              { label: t('max_users', 'Max Users'), value: sub.max_users },
              { label: t('allowed_languages', 'Languages'), value: sub.allowed_languages },
              { label: t('next_billing', 'Next Billing'), value: sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString() : '—' },
            ].map((item, i) => (
              <Box key={i}>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>{item.label}</Typography>
                {item.chip ? (
                  <Box><Chip label={item.value} size="small" sx={{ fontWeight: 600, bgcolor: item.chipProps.bg, color: item.chipProps.text, textTransform: 'capitalize' }} /></Box>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text, textTransform: item.capitalize ? 'capitalize' : 'none' }}>
                    {item.value ?? '—'}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
            {t('no_subscription', 'No subscription yet.')}
          </Typography>
        )}
      </Box>

      {/* Users & Invitations Table */}
      <Box sx={{
        p: 3, borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        bgcolor: theme.palette.background.paper,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.text }}>
              {t('users', 'Users')} ({organization.users?.length || 0})
            </Typography>
            {(organization.invitations?.length > 0) && (
              <Chip
                icon={<Mail size={12} />}
                label={`${organization.invitations.length} pending`}
                size="small"
                sx={{
                  fontWeight: 600, fontSize: '0.7rem',
                  bgcolor: alpha(colors.warning, 0.12),
                  color: colors.warning,
                  '& .MuiChip-icon': { color: colors.warning },
                }}
              />
            )}
          </Box>
          <Button
            size="small"
            startIcon={<UserPlus size={16} />}
            onClick={() => setInviteDialogOpen(true)}
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              bgcolor: colors.primary,
              fontWeight: 600,
            }}
          >
            {t('invite_user', 'Invite User')}
          </Button>
        </Box>
        {(organization.users?.length > 0 || organization.invitations?.length > 0) ? (
          <TableContainer sx={{ borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: colors.backgroundAlt }}>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('email', 'Email')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('name', 'Name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('roles', 'Roles')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('status.label', 'Status')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>{t('last_login', 'Last Login')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }} align="right">{t('actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Registered users */}
                {(organization.users || []).map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(colors.primary, 0.04) } }}
                    onClick={() => navigate(`/admin/organizations/${orgId}/users/${user.id}`)}
                  >
                    <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(user.roles || []).map((r) => <Chip key={r} label={r} size="small" sx={{ fontSize: '0.7rem' }} />)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active && user.is_allowed ? t('active', 'Active') : t('inactive', 'Inactive')}
                        size="small"
                        sx={{
                          fontWeight: 600, fontSize: '0.7rem',
                          bgcolor: user.is_active && user.is_allowed ? alpha(colors.secondary, 0.12) : alpha(colors.error, 0.12),
                          color: user.is_active && user.is_allowed ? colors.secondary : colors.error,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Analytics">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/organizations/${orgId}/users/${user.id}`); }}
                          sx={{ color: colors.primary }}
                        >
                          <BarChart3 size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Pending invitations */}
                {(organization.invitations || []).map((inv) => (
                  <TableRow
                    key={`inv-${inv.id}`}
                    sx={{ opacity: 0.75, bgcolor: alpha(colors.warning, 0.02) }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Clock size={13} color={colors.warning} />
                        <Typography variant="body2">{inv.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
                        {t('invitation_pending', 'Invitation pending')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(inv.roles || []).map((r) => <Chip key={r} label={r} size="small" sx={{ fontSize: '0.7rem' }} />)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t('pending', 'Pending')}
                        size="small"
                        sx={{
                          fontWeight: 600, fontSize: '0.7rem',
                          bgcolor: alpha(colors.warning, 0.12),
                          color: colors.warning,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
                        {inv.expires_at
                          ? `Expires ${new Date(inv.expires_at).toLocaleDateString()}`
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {inv.inviter_name && (
                          <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.7rem', mr: 0.5 }}>
                            by {inv.inviter_name}
                          </Typography>
                        )}
                        <Tooltip title={t('resend_invitation', 'Resend invitation')}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleResendInvitation(inv); }}
                            sx={{ color: colors.primary }}
                          >
                            <RefreshCw size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('delete_invitation', 'Delete invitation')}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDeleteInvitation(inv); }}
                            sx={{ color: colors.error }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontStyle: 'italic' }}>
            {t('no_users_in_org', 'No users in this organization yet.')}
          </Typography>
        )}
      </Box>

      {/* Subscription Dialog */}
      {organization && (
        <EditSubscriptionDialog
          open={subDialogOpen}
          onClose={() => setSubDialogOpen(false)}
          subscription={sub}
          organizationId={organization.id}
          onCreateSubscription={handleCreateSubscription}
          onUpdateSubscription={handleUpdateSubscription}
          colors={colors}
          t={t}
        />
      )}

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => { setInviteDialogOpen(false); setInviteError(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
          {t('invite_user', 'Invite User')}
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2 }}>
          <DialogContentText sx={{ mb: 3, color: colors.textSecondary }}>
            {t('invite_user_to_org', 'Send an email invitation to join this organization.')}
          </DialogContentText>

          <TextField
            autoFocus
            label={t('email_address', 'Email Address')}
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); }}
            error={!!inviteError}
            helperText={inviteError}
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />

          <FormControl fullWidth>
            <InputLabel>{t('roles', 'Roles')}</InputLabel>
            <Select
              multiple
              value={inviteRoles}
              onChange={handleInviteRoleChange}
              input={<OutlinedInput label={t('roles', 'Roles')} sx={{ borderRadius: '8px' }} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const chipColor = getRoleChipColor(value);
                    return (
                      <Chip
                        key={value}
                        label={value.replace('_', ' ')}
                        sx={{ bgcolor: chipColor.bg, color: chipColor.color, fontWeight: 500, textTransform: 'capitalize' }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role} sx={{ textTransform: 'capitalize' }}>
                  {role.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={() => { setInviteDialogOpen(false); setInviteError(''); }}
            sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            disableElevation
            disabled={!inviteEmail || !inviteRoles.length || inviteLoading}
            sx={{
              bgcolor: colors.primary, color: 'white', textTransform: 'none',
              fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
              '&:hover': { bgcolor: alpha(colors.primary, 0.85) },
              '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
            }}
          >
            {inviteLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {t('sending', 'Sending...')}
              </Box>
            ) : (
              t('send_invitation', 'Send Invitation')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomAlert
        open={alertInfo.open}
        message={alertInfo.message}
        severity={alertInfo.severity}
        onClose={() => setAlertInfo({ ...alertInfo, open: false })}
      />
    </Box>
  );
};

export default OrganizationDetailPage;
